import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShieldIcon from "@mui/icons-material/Shield";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/* ── Types ─────────────────────────────────────────── */

interface HistoryPoint {
  date: string;
  close?: number;
  dma20?: number;
  dma50?: number;
  volume?: number;
  rsi_14d?: number;
  [key: string]: any;
}

interface TechnicalData {
  ticker?: string;
  signal?: string;
  verdict?: { label?: string; summary?: string; action?: string; conviction?: number };
  latest?: { price?: number; close?: number; rsi_14d?: number; rvol?: number; avg_20d_volume?: number };
  moving_averages?: { dma20?: number; dma50?: number; price_vs_9dma_pct?: number; price_vs_20dma_pct?: number; price_vs_50dma_pct?: number };
  scores?: { trend_score?: number; momentum_score?: number; volume_score?: number; risk_score?: number; technical_score?: number };
  levels?: { support?: number; resistance?: number; stop_loss?: number; entry_zone?: { low?: number; high?: number } };
  analysis?: {
    trend?: { status?: string; summary?: string };
    momentum?: { status?: string };
    volume?: { summary?: string };
    risk?: { status?: string; summary?: string };
  };
  trade_plan?: { confirmation_trigger?: string; preferred_strategy?: string };
  institutional_view?: { bull_case?: unknown; bear_case?: unknown; portfolio_fit?: unknown };
  history?: HistoryPoint[];
}

/**
 * The analysis generator historically sent these fields as string arrays, but
 * some uploads contain one string or an object instead.  Keep the dashboard
 * render-safe while showing every usable value from either format.
 */
const toDisplayList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(toDisplayList);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    // Support a JSON-encoded array/object without showing the raw JSON text.
    if ((trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
      try {
        return toDisplayList(JSON.parse(trimmed));
      } catch {
        // A normal sentence can legitimately contain braces; display it below.
      }
    }
    return [trimmed];
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(toDisplayList);
  }

  return value == null ? [] : [String(value)];
};

/* ── Shared card style ─────────────────────────────── */
const cardSx = {
  borderRadius: 3,
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  p: 3,
  background: "#fff",
  minWidth: 0,
  boxSizing: "border-box",
};

// Technical statuses are API-provided and may be long underscore-separated strings.
// Allow them to wrap within their card rather than widening the Grid item.
const dynamicTextSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  whiteSpace: "normal",
};

/* ── Main component ────────────────────────────────── */
export default function TechnicalAnalysisDashboard({ data, runDate }: { data: TechnicalData; runDate?: string }) {
  const latest = data.latest || {};
  const ma = data.moving_averages || {};
  const scores = data.scores || {};
  const levels = data.levels || {};
  const analysis = data.analysis || {};
  const view = data.institutional_view || {};
  const bullCase = toDisplayList(view.bull_case);
  const bearCase = toDisplayList(view.bear_case);
  const portfolioFit = toDisplayList(view.portfolio_fit);
  const history = (data.history || [])
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-40);
  const asOfDate = history.length > 0
    ? new Date(history[history.length - 1].date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <Box sx={{ background: "#f8fafc", p: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>

        {/* Header */}
        <Grid item xs={12}>
          {runDate && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Typography variant="caption" sx={{ color: "#4f46e5", background: "#eef2ff", px: 1.5, py: 0.5, borderRadius: 2, fontWeight: 600, border: "1px solid #c7d2fe" }}>
                Run Date: {new Date(runDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </Typography>
            </Box>
          )}
          <Paper sx={cardSx}>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 3, alignItems: "flex-start", minWidth: 0 }}>
              <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                  <Typography variant="h3" fontWeight={800} sx={{ ...dynamicTextSx, letterSpacing: "-0.03em" }}>{data.ticker}</Typography>
                  <Chip
                    label={data.verdict?.label || data.signal || "—"}
                    size="small"
                    sx={{
                      background: "#fef3c7",
                      color: "#92400e",
                      border: "1px solid #fde68a",
                      fontWeight: 600,
                      maxWidth: "100%",
                      height: "auto",
                      "& .MuiChip-label": { ...dynamicTextSx, display: "block", py: 0.25 },
                    }}
                  />
                </Box>
                <Typography variant="body1" sx={{ ...dynamicTextSx, color: "#000000", maxWidth: 680 }}>
                  {data.verdict?.summary}
                </Typography>
                {asOfDate && (
                  <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                    <span style={{ color: "#211c80", fontWeight: 500 }}>Note: </span>
                    <span style={{ color: "#4f46e5" }}>Technical data as of {asOfDate}, using 40 trading days of technical history for charts</span>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", flex: "1 1 360px", minWidth: 0, gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" }, justifyContent: "flex-end" }}>
                {[
                  { label: "Price", value: `$${latest.price ?? latest.close ?? "N/A"}` },
                  { label: "Technical Score", value: scores.technical_score ?? "N/A" },
                  { label: "RSI 14D", value: latest.rsi_14d ?? "N/A" },
                  { label: "RVOL", value: latest.rvol ?? "N/A" },
                ].map((s) => (
                  <Box key={s.label} sx={{ background: "#f1f5f9", borderRadius: 2, px: 2, py: 1.2, textAlign: "right", minWidth: 0, flex: "1 1 90px" }}>
                    <Typography variant="body2" sx={{ color: "#000000", fontWeight: 500, display: "block", mb: 0.2 }}>{s.label}</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#000000" }}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Score Cards */}
        {[
          { title: "Trend Score", value: scores.trend_score, sub: analysis.trend?.status, icon: <TrendingUpIcon sx={{ fontSize: 28, color: "#16a34a" }} />, bg: "#f0fdf4", border: "#bbf7d0" },
          { title: "Momentum Score", value: scores.momentum_score, sub: analysis.momentum?.status, icon: <TrendingDownIcon sx={{ fontSize: 28, color: "#16a34a" }} />, bg: "#f0fdf4", border: "#bbf7d0" },
          { title: "Volume Score", value: scores.volume_score, sub: `RVOL ${latest.rvol ?? "N/A"} | ${analysis.volume?.summary ? "Weak" : ""}`, icon: <BarChartIcon sx={{ fontSize: 28, color: "#d97706" }} />, bg: "#fffbeb", border: "#fde68a" },
          { title: "Risk Score", value: scores.risk_score, sub: analysis.risk?.status, icon: <ShieldIcon sx={{ fontSize: 28, color: "#dc2626" }} />, bg: "#fff1f2", border: "#fecaca" },
        ].map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.title}>
            <Paper sx={{ ...cardSx, background: c.bg, border: `1px solid ${c.border}`, p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: "#000000", fontWeight: 500, mb: 0.5 }}>{c.title}</Typography>
                  <Typography variant="h3" fontWeight={700} sx={{ color: "#000000", lineHeight: 1.1, mb: 0.5 }}>{c.value ?? "N/A"}</Typography>
                  <Typography variant="caption" component="div" sx={{ ...dynamicTextSx, color: "#000000", lineHeight: 1.35 }}>{c.sub}</Typography>
                </Box>
                <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                  {c.icon}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Score Definitions */}
        <Grid item xs={12}>
          <Accordion sx={{ borderRadius: "12px !important", border: "1px solid #e2e8f0", boxShadow: "none", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#4f46e5" }} />} sx={{ px: 3, py: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 20, color: "#4f46e5" }} />
                <Typography fontWeight={600} sx={{ color: "#000000" }}>How are scores calculated?</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Grid container spacing={2}>
                {[
                  {
                    title: "Trend Score",
                    desc: "Measures the strength and quality of the stock trend using moving averages and price structure.",
                    factors: ["Price above/below 20DMA, 50DMA, 200DMA", "DMA alignment (bullish or bearish stacking)", "Trend continuation quality", "Higher highs / higher lows", "Pullback behavior"],
                    high: "Strong uptrend with healthy structure and sustained price strength.",
                    low: "Weak trend, broken structure, or bearish moving average alignment.",
                  },
                  {
                    title: "Momentum Score",
                    desc: "Measures the speed and persistence of price movement.",
                    factors: ["RSI behavior", "Monthly / quarterly / YTD performance", "Acceleration vs exhaustion", "Momentum continuation quality", "Overbought / oversold conditions"],
                    high: "Strong institutional momentum with sustained price expansion.",
                    low: "Weak momentum, fading strength, or trend exhaustion.",
                  },
                  {
                    title: "Volume Score",
                    desc: "Measures the level of institutional participation behind the move.",
                    factors: ["Relative Volume (RVOL)", "Volume expansion on breakouts", "Accumulation vs weak participation", "Confirmation of price action"],
                    high: "Strong institutional buying and volume confirmation.",
                    low: "Weak participation, low conviction, or unconfirmed moves.",
                  },
                  {
                    title: "Risk Score",
                    desc: "Measures volatility and downside risk profile.",
                    factors: ["ATR percentage", "Historical volatility", "Daily price swings", "Gap risk", "Price instability"],
                    high: "Higher volatility and greater trading risk.",
                    low: "More stable and lower-risk price behavior.",
                  },
                  {
                    title: "Technical Score",
                    desc: "Overall institutional technical rating derived from trend quality, momentum strength, volume confirmation, volatility risk, and price structure.",
                    factors: ["80–100 → Strong institutional setup", "65–79 → Bullish / tradable", "50–64 → Watchlist / mixed setup", "35–49 → Weak technical structure", "0–34 → Avoid / bearish setup"],
                    high: "Strong across all dimensions — high conviction trade.",
                    low: "Weak across multiple dimensions — avoid or monitor only.",
                  },
                ].map((s) => (
                  <Grid item xs={12} sm={6} md={4} key={s.title} sx={{ display: "flex" }}>
                    <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2.5, width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography fontWeight={700} sx={{ color: "#000000", fontSize: "0.95rem" }}>{s.title}</Typography>
                      <Typography variant="body2" sx={{ color: "#000000" }}>{s.desc}</Typography>
                      <Box>
                        <Typography variant="caption" fontWeight={600} sx={{ color: "#000000", display: "block", mb: 0.5 }}>Factors considered:</Typography>
                        {s.factors.map((f) => (
                          <Typography key={f} variant="caption" sx={{ color: "#000000", display: "flex", gap: 0.5, mb: 0.3 }}>
                            <span>•</span><span>{f}</span>
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ mt: "auto", pt: 1, borderTop: "1px solid #e2e8f0" }}>
                        <Typography variant="caption" sx={{ color: "#000000", display: "block" }}><strong>↑ Higher:</strong> {s.high}</Typography>
                        <Typography variant="caption" sx={{ color: "#000000", display: "block", mt: 0.3 }}><strong>↓ Lower:</strong> {s.low}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Price vs DMA */}
        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Typography variant="h6" fontWeight={600} mb={2}>Price vs DMA Snapshot</Typography>
            <Grid container spacing={2}>
              {[
                {
                  label: "Price vs 9DMA",
                  value: ma.price_vs_9dma_pct != null ? `${ma.price_vs_9dma_pct > 0 ? "+" : ""}${ma.price_vs_9dma_pct}%` : "N/A",
                  note: "Short-term momentum cooling",
                  color: (ma.price_vs_9dma_pct ?? 0) >= 0 ? "#16a34a" : "#dc2626",
                },
                {
                  label: "Price vs 20DMA",
                  value: ma.price_vs_20dma_pct != null ? `${ma.price_vs_20dma_pct > 0 ? "+" : ""}${ma.price_vs_20dma_pct}%` : "N/A",
                  note: "Holding short-term support",
                  color: (ma.price_vs_20dma_pct ?? 0) >= 0 ? "#16a34a" : "#dc2626",
                },
                {
                  label: "Price vs 50DMA",
                  value: ma.price_vs_50dma_pct != null ? `${ma.price_vs_50dma_pct > 0 ? "+" : ""}${ma.price_vs_50dma_pct}%` : "N/A",
                  note: "Strong medium-term extension",
                  color: (ma.price_vs_50dma_pct ?? 0) >= 0 ? "#16a34a" : "#dc2626",
                },
                { label: "Breakout Status", value: "No Confirmation", note: `Needs RVOL > 1.5 above ${levels.resistance ?? "resistance"}`, color: "#d97706" },
              ].map((d) => (
                <Grid item xs={12} sm={6} md={3} key={d.label}>
                  <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" color="text.primary" display="block">{d.label}</Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ color: d.color, my: 0.5 }}>{d.value}</Typography>
                    <Typography variant="caption" color="text.primary">{d.note}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Historical Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ShowChartIcon sx={{ color: "#4f46e5" }} />
              <Typography variant="h6" fontWeight={600}>Historical Price, DMA & Levels</Typography>
            </Box>
            <Box sx={{ height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={history} margin={{ top: 20, right: 24, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 10, fill: "#000000" }} interval={Math.ceil(history.length / 8) - 1} height={35}
                    tickFormatter={(val: string) => {
                      const d = new Date(val);
                      return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
                    }}
                  />
                  <YAxis tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 11, fill: "#000000" }} domain={["auto", "auto"]} />
                  <Tooltip formatter={(v: number, name: string) => [`$${v}`, name]} labelFormatter={(label: string) => { const d = new Date(label); return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }} />
                  <Legend formatter={(value) => {
                    if (value === "Price") return `Price ($${data.latest?.price})`;
                    if (value === "20DMA") return `20DMA ($${data.moving_averages?.dma20})`;
                    if (value === "50DMA") return `50DMA ($${data.moving_averages?.dma50})`;
                    return value;
                  }} />
                  {levels.support && (
                    <ReferenceLine y={levels.support} stroke="#10b981" strokeDasharray="6 4" strokeWidth={1.5}
                      label={{ value: `Support $${levels.support}`, position: "insideTopLeft", fontSize: 11, fill: "#10b981", fontWeight: 600 }} />
                  )}
                  {levels.resistance && (
                    <ReferenceLine y={levels.resistance} stroke="#f59e0b" strokeDasharray="6 4" strokeWidth={1.5}
                      label={{ value: `Resistance $${levels.resistance}`, position: "insideTopLeft", fontSize: 11, fill: "#d97706", fontWeight: 600 }} />
                  )}
                  <Line type="monotone" dataKey="close" stroke="#000000" strokeWidth={2.5} dot={{ r: 2 }} name="Price" />
                  <Line type="monotone" dataKey="dma20" stroke="#2563eb" strokeWidth={2} dot={false} name="20DMA" />
                  <Line type="monotone" dataKey="dma50" stroke="#7c3aed" strokeWidth={2} dot={false} name="50DMA" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* PM Decision */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...cardSx, height: "100%", boxSizing: "border-box" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PsychologyIcon sx={{ color: "#4f46e5" }} />
              <Typography variant="h6" fontWeight={600}> Final Decision</Typography>
            </Box>
            <Box sx={{ background: "#fffbeb", borderRadius: 2, border: "1px solid #fde68a", p: 2.5, mb: 2, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: "#92400e", fontWeight: 600 }}>Final View</Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: "#78350f",
                  mt: 0.5,
                  maxWidth: "100%",
                  fontSize: "1.15rem",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                  wordBreak: "normal",
                }}
              >
                {data.verdict?.label?.replace(/_/g, " ")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#000000", mt: 1 }}>{data.verdict?.action}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="h6" color="text.primary">Institutional conviction</Typography>
              <Typography variant="h6" fontWeight={600}>{data.verdict?.conviction} / 10</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(data.verdict?.conviction || 0) * 10}
              sx={{ height: 8, borderRadius: 4, background: "#e2e8f0", "& .MuiLinearProgress-bar": { background: "#000000", borderRadius: 4 } }}
            />
            <Box sx={{ mt: 2, background: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", p: 2 }}>
              <Typography variant="h6" color="text.primary" display="block">Confirmation trigger</Typography>
              <Typography variant="body2" mt={0.5}>{data.trade_plan?.confirmation_trigger}</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Trade Plan */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Paper sx={{ ...cardSx, width: "100%" }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Trade Plan</Typography>
            <Grid container spacing={1.5}>
              {[
                { label: "Ideal Entry", value: `${levels.entry_zone?.low ?? "-"} - ${levels.entry_zone?.high ?? "-"}` },
                { label: "Stop Loss", value: `Below ${levels.stop_loss ?? "-"}` },
                { label: "Target 1", value: `${levels.entry_zone?.high ? Math.round(levels.entry_zone.high * 1.12) + " - " + Math.round(levels.entry_zone.high * 1.17) : "110 - 115"}` },
                { label: "Target 2", value: "120+" },
              ].map((t) => (
                <Grid item xs={6} key={t.label}>
                  <Box sx={{ background: "#f1f5f9", borderRadius: 2, p: 2, minHeight: 64 }}>
                    <Typography variant="body1" sx={{ color: "#000000", fontWeight: 500 }} display="block">{t.label}</Typography>
                    <Typography variant="body1" fontWeight={700} mt={0.5} sx={{ color: "#000000" }}>{t.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 2, background: "#fffbeb", borderRadius: 2, border: "1px solid #fde68a", p: 2 }}>
              <Typography variant="body2" sx={{ color: "#92400e" }}>{data.trade_plan?.preferred_strategy}</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Trend Interpretation */}
        <Grid item xs={12} md={8} sx={{ display: "flex" }}>
          <Paper sx={{ ...cardSx, width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ShowChartIcon sx={{ color: "#4f46e5" }} />
              <Typography variant="h6" fontWeight={600}>Historical Trend Interpretation</Typography>
            </Box>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#f0fdf4" border="#86efac" titleColor="#166534" title="Trend" text={analysis.trend?.summary} />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#fffbeb" border="#fde68a" titleColor="#92400e" title="Resistance" text={`The key breakout zone is near ${levels.resistance}. A clean move above this level needs stronger RVOL.`} />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#f8fafc" border="#cbd5e1" titleColor="#000000" title="Support" text={`Support is near ${levels.support}. Pullbacks near ${levels.entry_zone?.low}-${levels.entry_zone?.high} are preferable to chasing strength.`} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Volume Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={cardSx}>
            <Typography variant="h6" fontWeight={600} mb={2}>Volume Trend</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 10, fill: "#000000" }} interval={Math.ceil(history.length / 8) - 1} height={35}
                    tickFormatter={(val: string) => { const d = new Date(val); return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }); }}
                  />
                  <YAxis tickFormatter={(v: number) => `${(v / 1_000_000).toFixed(1)}M`} tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 10, fill: "#000000" }} />
                  <Tooltip />
                  <Bar dataKey="volume" radius={[4, 4, 0, 0]}>
                    {history.map((entry, index) => {
                      const prev = index === 0 ? null : history[index - 1]?.volume;
                      const isGreen = prev === null || (entry.volume ?? 0) >= (prev ?? 0);
                      return <Cell key={`cell-${index}`} fill={isGreen ? "#22c55e" : "#ef4444"} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* RSI Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={cardSx}>
            <Typography variant="h6" fontWeight={600} mb={2}>RSI Trend</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 10, fill: "#000000" }} interval={Math.ceil(history.length / 8) - 1} height={35}
                    tickFormatter={(val: string) => { const d = new Date(val); return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }); }}
                  />
                  <YAxis domain={[20, 90]} tickLine={{ stroke: "#cbd5e1" }} axisLine={{ stroke: "#000000", strokeWidth: 1 }} tick={{ fontSize: 10, fill: "#000000" }} />
                  <Tooltip />
                  <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "70", fontSize: 10 }} />
                  <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: "50", fontSize: 10 }} />
                  <ReferenceLine y={30} stroke="#10b981" strokeDasharray="4 4" label={{ value: "30", fontSize: 10 }} />
                  <Area type="monotone" dataKey="rsi_14d" stroke="#000000" fill="#e2e8f0" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Bull Case */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...cardSx, background: "#f0fdf4", border: "1px solid #86efac" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: "#14532d" }}>Bull Case</Typography>
            </Box>
            {bullCase.map((item, index) => (
              <Typography key={`${item}-${index}`} variant="body2" sx={{ color: "#166534", mb: 0.75, display: "flex", gap: 0.5 }}>
                <span>•</span><span>{item}</span>
              </Typography>
            ))}
          </Paper>
        </Grid>

        {/* Bear Case */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...cardSx, background: "#fff1f2", border: "1px solid #fca5a5" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <WarningAmberIcon sx={{ color: "#dc2626" }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: "#7f1d1d" }}>Bear Case</Typography>
            </Box>
            {bearCase.map((item, index) => (
              <Typography key={`${item}-${index}`} variant="body2" sx={{ color: "#991b1b", mb: 0.75, display: "flex", gap: 0.5 }}>
                <span>•</span><span>{item}</span>
              </Typography>
            ))}
          </Paper>
        </Grid>

        {/* Portfolio Fit */}
        <Grid item xs={12} md={4}>
          <Paper sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <AccountBalanceWalletIcon sx={{ color: "#4f46e5" }} />
              <Typography variant="h6" fontWeight={700}>Portfolio Fit</Typography>
            </Box>
            <Typography variant="body1" color="text.primary" mb={1.5}>
              {portfolioFit.join(", ")}.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {portfolioFit.map((item, index) => (
                <Chip key={`${item}-${index}`} label={item} size="small" variant="outlined" />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Institutional Summary */}
        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Typography variant="h6" fontWeight={600} mb={2}>Institutional Summary</Typography>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#f0fdf4" border="#86efac" titleColor="#166534" title="What is attractive?" text={analysis.trend?.summary} />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#fffbeb" border="#fde68a" titleColor="#92400e" title="What needs confirmation?" text={`${analysis.volume?.summary ?? ""} ${data.trade_plan?.confirmation_trigger ?? ""}.`} />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <InfoBox bg="#f8fafc" border="#cbd5e1" titleColor="#000000" title="What is the risk?" text={analysis.risk?.summary} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}

/* ── Sub-components ────────────────────────────────── */

function InfoBox({ bg, border, titleColor, title, text }: {
  bg: string; border: string; titleColor: string; title: string; text?: string;
}) {
  return (
    <Box sx={{ background: bg, border: `1px solid ${border}`, borderRadius: 3, p: 3, width: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: titleColor, mb: 1.5 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: "#000000", lineHeight: 1.8, flexGrow: 1 }}>{text}</Typography>
    </Box>
  );
}
